import React, { useCallback, useEffect, useMemo } from 'react'
import * as yup from 'yup'
import { useModal } from '~/hooks'
import { useApi } from '~/store/store'
import { useForm } from 'react-hook-form'
import { mutualTheme } from '~/utils/theme'
import { isValid } from '@fnando/cnpj'
import { useHistory } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { OnboardingFormLayout, HfTextInput, HfCurrencyInput, HfUsernameInput, Dialog } from '~/components'
import { Button, CircularProgress, InputAdornment, Typography } from '@material-ui/core'
import { CreateAccountFormValues, useCreateAccountFormStore } from '~/store/createAccountForm'
import { toDayStringInBrazil } from '~/utils/date'
import { OnboardingWizardActions, trackEvent } from '~/service/analytics'
import { transparentize } from 'polished'
import { SearchIcon } from '~/assets/icons'
import { ErrorModal } from './components'

const validationSchema = yup.object().shape({
  cnpj: yup.string()
    .required('Campo obrigatório')
    .test('If username is a valid CNPJ', 'CNPJ inválido',
      cnpj => typeof cnpj === 'string' ? isValid(cnpj) : false),
  companyName: yup.string().required('Campo obrigatório'),
  monthlyIncome: yup.string().required('Campo obrigatório')
})

const mapErrorToTrackEvent = (error: string): OnboardingWizardActions => {
  switch (error) {
    case 'COMPANY_INVALID_STATUS':
      return OnboardingWizardActions.INACTIVE_CNPJ
    case 'COMPANY_INVALID_AGE':
      return OnboardingWizardActions.IS_NEW_CNPJ
    case 'COMPANY_IS_MEI':
      return OnboardingWizardActions.IS_SMALL_BUSINESS
    case 'COMPANY_INVALID_CNPJ':
      return OnboardingWizardActions.INVALID_CNPJ
    default:
      return OnboardingWizardActions.INVALID_CNPJ
  }
}

export const CompanyInfo: React.FC = () => {
  const [isModalOpen, toggleModal] = useModal()
  const { createAccountFormState, updateForm } = useCreateAccountFormStore()
  const [getCompaniesBasicData, getCompaniesBasicDataState] = useApi('getCompaniesBasicData')
  const { form } = createAccountFormState
  const history = useHistory()

  const { loading, error, data: company } = getCompaniesBasicDataState
  const isInvalidCnpj = useMemo(() => error && error.code === 'INVALID_CNPJ', [error])

  const { handleSubmit, control, formState: { errors }, watch, setValue, clearErrors, setError } = useForm<CreateAccountFormValues>({
    defaultValues: form,
    resolver: yupResolver(validationSchema)
  })

  const cnpj = watch('cnpj')

  useEffect(() => {
    if (form.responsibleName === '') {
      history.push('/register')
    }
  }, [form])

  useEffect(() => {
    clearErrors('cnpj')
    setValue('companyName', '', { shouldDirty: true })
  }, [cnpj])

  const onSubmit = useCallback(async (formData: CreateAccountFormValues) => {
    updateForm({ ...formData, foundationDate: toDayStringInBrazil(company ? company.foundationDate : '') })
    trackEvent(OnboardingWizardActions.ON_COMPANY_DATA_NEXT_CLICK)
    history.push('/register/loan-info')
  }, [company, updateForm, history])

  const getError = useMemo(() => {
    if (error) {
      return error ? (error.statusCode === 500 ? 'Houve um problema na busca do seu CNPJ' : error.data) : 'Houve um problema na busca do seu CNPJ'
    }

    return null
  }, [error])

  const onSearchCnpj = useCallback(async () => {
    clearErrors('cnpj')

    if (!isValid(cnpj)) {
      setError('cnpj', { message: 'CNPJ inválido' })
      return
    }

    const { data: companyInfo, error: companyError } = await getCompaniesBasicData({ cnpj })
    trackEvent(OnboardingWizardActions.ON_SEARCH_CNPJ_CLICK)

    if (companyError) {
      if (companyError.statusCode !== 200) {
        setError('cnpj', { message: 'CNPJ não encontrado' })
        return
      }

      toggleModal()
      const mappedErrorToAction = mapErrorToTrackEvent(companyError.code)
      trackEvent(mappedErrorToAction)
      return
    }

    if (companyInfo) {
      const { name } = companyInfo
      name && setValue('companyName', name, { shouldDirty: true })
      name && setValue('fantasyName', name, { shouldDirty: true })
      trackEvent(OnboardingWizardActions.VALID_CNPJ)
    }
  }, [getCompaniesBasicData, cnpj, toggleModal, setValue])

  const redirectToLoginWithCnpj = useCallback(() => {
    history.push({
      pathname: '/',
      state: cnpj
    })
  }, [cnpj])

  const onBackClick = useCallback(() => {
    history.goBack()
  }, [history])

  return (
    <OnboardingFormLayout
      title='Qual a sua empresa?'
      subtitle='Informe os dados da sua empresa abaixo:'
      canContinue
      onBackClick={onBackClick}
      onNextClick={handleSubmit(onSubmit)}
    >
      <HfUsernameInput
        name='cnpj'
        acceptCNPJ
        label='CNPJ'
        control={control}
        error={errors.cnpj?.message}
        errorMessage={errors.cnpj?.message}
        placeholder='99.999.999/9999-99'
        subtitle='Atenção: se você é MEI, pedimos que faça a solicitação no nosso aplicativo para pessoa física.'
        endAdornment={(
          <InputAdornment position='end'>
            <Button
              variant='contained'
              color='secondary'
              size='large'
              disabled={loading}
              startIcon={<SearchIcon />}
              onClick={onSearchCnpj}
              sx={{
                color: 'secondary.main',
                backgroundColor: 'badgeBorderColor.info.main',
                height: 40,
                width: 94,
                '&:hover': {
                  backgroundColor: transparentize(0.20, mutualTheme.palette.badgeBorderColor.info.main)
                }
              }}
            >
              {(loading)
                ? (
                  <CircularProgress color='inherit' size={20} />
                )
                : (
                  <Typography variant='airbnbBody2' sx={{ fontWeight: 600 }}>Buscar</Typography>
                )}
            </Button>
          </InputAdornment>
        )}
      />

      <HfTextInput
        readOnly
        control={control}
        name='companyName'
        label='Razão Social'
        error={errors.companyName?.message}
        errorMessage={errors.companyName?.message}
        placeholder='Preenchimento automático'
      />

      <HfTextInput
        control={control}
        name='fantasyName'
        label='Nome fantasia'
        error={errors.fantasyName?.message}
        errorMessage={errors.fantasyName?.message}
        placeholder='Preenchimento automático'
        subtitle='Escolha o nome de sua preferência para nos referirmos a sua empresa.'
      />

      <HfCurrencyInput
        control={control}
        name='monthlyIncome'
        error={errors.monthlyIncome?.message}
        errorMessage={errors.monthlyIncome?.message}
        label='Faturamento mensal'
        placeholder='R$ 999.999,99'
      />

      <Dialog
        maxWidth='xs'
        isVisible={isModalOpen}
        onClose={toggleModal}
        disableClose={loading}
      >
        <ErrorModal
          description={getError}
          buttonText={isInvalidCnpj ? 'Entendi' : 'Fazer Login'}
          title='Não podemos prosseguir com o pedido de empréstimo'
          onClick={isInvalidCnpj ? toggleModal : redirectToLoginWithCnpj}
        />
      </Dialog>
    </OnboardingFormLayout>
  )
}
